import { useEffect, useRef, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  Heart, ChevronLeft, Camera, Mic, Trash2, Copy, MessageCircle,
  Mail, MessageSquare, QrCode, X, Loader2, Square, Sparkles, Clock, Lock
} from "lucide-react";
import Envelope from "@/components/Envelope";
import PaperTexture from "@/components/PaperTexture";
import Stamp, { STAMPS } from "@/components/Stamp";
import DeliveringAnimation from "@/components/DeliveringAnimation";
import { CATEGORIES, EMOTIONS, emotionById } from "@/lib/constants";
import { uploadFile, createLetter, trackShare } from "@/lib/api";
import { runCheckout, currentLetterPriceInr } from "@/lib/payment";

const REGULAR_STEPS = ["category", "write", "emotion", "memories", "stamp", "seal", "delivering", "share"];
const CAPSULE_STEPS = ["write", "emotion", "memories", "stamp", "capsule", "seal", "delivering", "share"];

const DATE_PRESETS = [
  { id: "1m", label: "In 1 month", days: 30 },
  { id: "6m", label: "In 6 months", days: 183 },
  { id: "1y", label: "In 1 year", days: 365 },
  { id: "5y", label: "In 5 years", days: 365 * 5 },
];

export default function Compose() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isCapsule = params.get("mode") === "capsule";
  const STEPS = isCapsule ? CAPSULE_STEPS : REGULAR_STEPS;

  const [step, setStep] = useState(STEPS[0]);
  const [category, setCategory] = useState(isCapsule ? { id: "just_because", label: "Future me" } : null);
  const [emotion, setEmotion] = useState("warm_love");
  const [senderName, setSenderName] = useState("");
  const [recipientName, setRecipientName] = useState(isCapsule ? "Future me" : "");
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState([]);
  const [voice, setVoice] = useState(null);
  const [stamp, setStamp] = useState("red_heart");
  const [deliverAt, setDeliverAt] = useState(null); // ISO string
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [slug, setSlug] = useState(null);
  const [manageToken, setManageToken] = useState(null);
  const startTimeRef = useRef(Date.now());
  const emo = emotionById(emotion);

  const link = slug ? `${window.location.origin}/letter/${slug}` : "";
  const manageLink = slug && manageToken ? `${window.location.origin}/letter/${slug}?manage=${manageToken}` : "";

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const res = await uploadFile(file);
      setUploading(false);
      return { file_id: res.file_id, previewUrl: URL.createObjectURL(file) };
    } catch (e) {
      setUploading(false);
      toast.error("Upload failed. Please try again.");
      return null;
    }
  };

  const goNext = () => {
    const i = STEPS.indexOf(step);
    if (i < STEPS.length - 1) setStep(STEPS[i + 1]);
  };
  const goBack = () => {
    const i = STEPS.indexOf(step);
    if (i > 0) setStep(STEPS[i - 1]);
  };

  const submit = async () => {
    if (!senderName.trim() || !recipientName.trim()) {
      toast.error("Please add your name and someone special's name.");
      setStep("write");
      return;
    }
    if (!body.trim()) {
      toast.error("Your letter is empty.");
      setStep("write");
      return;
    }
    if (isCapsule && !deliverAt) {
      toast.error("Please choose an unlock date.");
      setStep("capsule");
      return;
    }
    setSubmitting(true);
    try {
      const writing_seconds = Math.round((Date.now() - startTimeRef.current) / 1000);
      const attachments = [
        ...photos.map((p) => ({ kind: "photo", file_id: p.file_id })),
        ...(voice ? [{ kind: "voice", file_id: voice.file_id }] : []),
      ];
      const letterPayload = {
        category: category?.id || "just_because",
        emotion,
        sender_name: senderName,
        recipient_name: recipientName,
        body,
        attachments,
        stamp,
        is_time_capsule: isCapsule,
        deliver_at: isCapsule ? deliverAt : null,
        language: navigator.language || "unknown",
        writing_seconds,
      };
      const res = await runCheckout({
        letterPayload,
        prefill: { name: senderName },
      });
      setSlug(res.slug);
      setManageToken(res.manage_token || null);
      setStep("delivering");
    } catch (e) {
      const msg = (e && e.message) || "";
      if (msg === "cancelled") {
        toast("Payment cancelled — your letter is still safe here.", { icon: "🕊" });
      } else {
        toast.error(msg || "Payment failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full cream-bg text-[#1C1917]">
      {/* Top bar */}
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <Link to="/" className="flex items-center gap-3 text-[#4c3f34] hover:text-[#1C1917] transition-colors" data-testid="compose-back-home">
          <ChevronLeft size={18} />
          <span className="font-serif-elegant text-xl">WrittenHearts</span>
        </Link>
        <div className="text-xs uppercase tracking-[0.35em] text-[#4c3f34]/60">
          {step === "share" ? "Delivered" : step.replace("_", " ")}
        </div>
        <div className="w-20" />
      </div>

      <AnimatePresence mode="wait">
        {step === "category" && (
          <motion.section key="cat"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="max-w-5xl mx-auto px-6 pb-24 pt-6"
          >
            <p className="uppercase tracking-[0.4em] text-xs text-[#991B1B]">Step 1</p>
            <h1 className="mt-3 font-serif-elegant text-4xl md:text-6xl leading-tight text-[#1C1917]">Who is this letter for?</h1>
            <p className="mt-3 text-[#4c3f34] max-w-xl">The atmosphere gently shifts to match this moment.</p>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  data-testid={`cat-${c.id}`}
                  onClick={() => { setCategory(c); goNext(); }}
                  className={`group text-left rounded-[4px] px-5 py-6 border transition-all hover:-translate-y-0.5 ${
                    category?.id === c.id
                      ? "bg-[#1C1917] text-[#FDFBF7] border-transparent"
                      : "bg-[#FDFBF7] border-[#1C1917]/10 text-[#1C1917] hover:border-[#991B1B]/30"
                  }`}
                >
                  <div className="text-3xl mb-3">
                    <Heart className="text-[#991B1B]" fill="#991B1B" size={20} />
                  </div>
                  <div className="font-serif-elegant text-xl">{c.label}</div>
                </button>
              ))}
            </div>
          </motion.section>
        )}

        {step === "write" && (
          <motion.section key="write"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-3xl mx-auto px-6 pb-24"
          >
            <div className="flex items-center justify-between mb-6">
              <button onClick={goBack} className="text-[#4c3f34] flex items-center gap-2 hover:text-[#1C1917]" data-testid="write-back-btn">
                <ChevronLeft size={16} /> back
              </button>
              <div className="text-xs text-[#4c3f34]/70">Step 2 · Write from the heart</div>
              <button
                onClick={goNext}
                disabled={!body.trim() || !senderName.trim() || !recipientName.trim()}
                data-testid="write-next-btn"
                className="text-sm px-5 py-2 rounded-full bg-[#1C1917] text-[#FDFBF7] disabled:opacity-40 hover:bg-[#3f342c] transition-colors"
              >
                Continue
              </button>
            </div>

            <PaperTexture className="p-8 md:p-14" style={{ minHeight: "70vh" }}>
              <div className="flex items-center justify-between text-[#1C1917]/70 text-sm">
                <div className="flex items-center gap-2">
                  <input
                    data-testid="write-sender-input"
                    className="bg-transparent border-b border-[#1C1917]/20 focus:border-[#1C1917] outline-none px-1 py-1 font-hand text-2xl text-[#1C1917] w-40"
                    placeholder="From"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#1C1917]/60 font-serif-elegant italic">to</span>
                  <input
                    data-testid="write-recipient-input"
                    className="bg-transparent border-b border-[#1C1917]/20 focus:border-[#1C1917] outline-none px-1 py-1 font-hand text-2xl text-[#1C1917] w-40 text-right"
                    placeholder="Someone special"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-8">
                <textarea
                  data-testid="write-body-input"
                  autoFocus
                  rows={16}
                  placeholder="Dear one,&#10;&#10;There is something I have wanted to tell you..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="w-full bg-transparent outline-none resize-none font-serif-elegant text-2xl md:text-3xl leading-relaxed placeholder:text-[#1C1917]/30"
                  style={{ color: emo.ink }}
                />
              </div>
              <div className="mt-6 text-right font-hand text-2xl" style={{ color: emo.ink }}>
                — {senderName || "you"}
              </div>
            </PaperTexture>
          </motion.section>
        )}

        {step === "emotion" && (
          <motion.section key="emo"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-5xl mx-auto px-6 pb-24"
          >
            <div className="flex items-center justify-between mb-6">
              <button onClick={goBack} className="text-[#4c3f34] flex items-center gap-2 hover:text-[#1C1917]" data-testid="emo-back-btn">
                <ChevronLeft size={16} /> back
              </button>
              <div className="text-xs text-[#4c3f34]/70">Step 3 · Choose the feeling</div>
              <button onClick={goNext} data-testid="emo-next-btn" className="text-sm px-5 py-2 rounded-full bg-[#1C1917] text-[#FDFBF7] hover:bg-[#3f342c] transition-colors">
                Continue
              </button>
            </div>
            <h2 className="font-serif-elegant text-4xl md:text-5xl">What does this letter carry?</h2>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              {EMOTIONS.map((e) => (
                <button
                  key={e.id}
                  data-testid={`emo-${e.id}`}
                  onClick={() => setEmotion(e.id)}
                  className={`rounded-[4px] p-5 text-left transition-all hover:-translate-y-0.5 border ${
                    emotion === e.id ? "border-transparent" : "bg-[#FDFBF7] border-[#1C1917]/10 hover:border-[#991B1B]/30"
                  }`}
                  style={emotion === e.id ? { background: e.tint, color: e.ink } : {}}
                >
                  <div className="w-9 h-9 rounded-full mb-4" style={{ background: e.accent }} />
                  <div className="font-serif-elegant text-2xl">{e.label}</div>
                </button>
              ))}
            </div>
          </motion.section>
        )}

        {step === "memories" && (
          <motion.section key="mem"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-4xl mx-auto px-6 pb-24"
          >
            <div className="flex items-center justify-between mb-6">
              <button onClick={goBack} className="text-[#4c3f34] flex items-center gap-2 hover:text-[#1C1917]" data-testid="mem-back-btn">
                <ChevronLeft size={16} /> back
              </button>
              <div className="text-xs text-[#4c3f34]/70">Step 4 · Memories (optional)</div>
              <button onClick={goNext} data-testid="mem-next-btn" className="text-sm px-5 py-2 rounded-full bg-[#1C1917] text-[#FDFBF7] hover:bg-[#3f342c] transition-colors">
                Continue
              </button>
            </div>

            <h2 className="font-serif-elegant text-4xl md:text-5xl">Tuck in a memory</h2>
            <p className="mt-3 text-[#4c3f34]">A photograph, or the sound of your voice. Nothing more.</p>

            <div className="mt-10 grid md:grid-cols-2 gap-6">
              <div className="rounded-[4px] bg-[#FDFBF7] border border-[#1C1917]/10 p-6">
                <div className="flex items-center gap-3 text-[#1C1917]">
                  <Camera size={18} /><span className="font-serif-elegant text-2xl">Photographs</span>
                </div>
                <p className="text-sm text-[#4c3f34] mt-2">They will appear as printed photos, tucked inside the envelope.</p>
                <label
                  data-testid="mem-photo-input-label"
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1C1917] text-[#FDFBF7] cursor-pointer hover:bg-[#3f342c] transition-colors text-sm"
                >
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                  {uploading ? "Uploading" : "Add photo"}
                  <input
                    data-testid="mem-photo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const p = await handleUpload(f);
                      if (p) setPhotos([...photos, p]);
                      e.target.value = "";
                    }}
                  />
                </label>
                <div className="mt-5 flex flex-wrap gap-3">
                  {photos.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p.previewUrl} alt="" className="w-24 h-24 object-cover rounded-[3px] border-4 border-[#FDFBF7] shadow-md" />
                      <button
                        data-testid={`mem-photo-delete-${i}`}
                        onClick={() => setPhotos(photos.filter((_, ix) => ix !== i))}
                        className="absolute -top-2 -right-2 bg-[#1C1917] text-white rounded-full p-1"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <VoiceRecorder voice={voice} setVoice={setVoice} uploadFn={handleUpload} />
            </div>

            <div className="mt-10 flex justify-end">
              <button
                data-testid="mem-stamp-btn"
                onClick={() => setStep("stamp")}
                className="px-6 py-3 rounded-full bg-[#991B1B] text-white hover:bg-[#7F1D1D] transition-colors"
              >
                Choose a stamp
              </button>
            </div>
          </motion.section>
        )}

        {step === "stamp" && (
          <motion.section key="stamp"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-5xl mx-auto px-6 pb-24"
          >
            <div className="flex items-center justify-between mb-6">
              <button onClick={goBack} className="text-[#4c3f34] flex items-center gap-2 hover:text-[#1C1917]" data-testid="stamp-back-btn">
                <ChevronLeft size={16} /> back
              </button>
              <div className="text-xs text-[#4c3f34]/70">{isCapsule ? "Step 4" : "Step 5"} · Pick a stamp</div>
              <button
                onClick={() => setStep(isCapsule ? "capsule" : "seal")}
                data-testid="stamp-next-btn"
                className="text-sm px-5 py-2 rounded-full bg-[#1C1917] text-[#FDFBF7] hover:bg-[#3f342c] transition-colors"
              >
                Continue
              </button>
            </div>

            <h2 className="font-serif-elegant text-4xl md:text-5xl">Pick a stamp</h2>
            <p className="mt-3 text-[#4c3f34]">Every letter deserves a small piece of art in the corner.</p>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-5">
              {STAMPS.map((s) => (
                <button
                  key={s.id}
                  data-testid={`stamp-${s.id}`}
                  onClick={() => setStamp(s.id)}
                  className={`p-4 rounded-[6px] transition-all hover:-translate-y-0.5 flex flex-col items-center gap-3 border ${
                    stamp === s.id
                      ? "bg-[#FDFBF7] border-[#991B1B] shadow-lg shadow-[#991B1B]/20"
                      : "bg-[#FDFBF7]/60 border-[#1C1917]/10 hover:border-[#991B1B]/40"
                  }`}
                >
                  <Stamp id={s.id} size={72} tilt={stamp === s.id ? 0 : -6} />
                  <div className="font-serif-elegant text-lg text-[#1C1917]">{s.label}</div>
                </button>
              ))}
            </div>
          </motion.section>
        )}

        {step === "capsule" && (
          <motion.section key="capsule"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-3xl mx-auto px-6 pb-24"
          >
            <div className="flex items-center justify-between mb-6">
              <button onClick={goBack} className="text-[#4c3f34] flex items-center gap-2 hover:text-[#1C1917]" data-testid="capsule-back-btn">
                <ChevronLeft size={16} /> back
              </button>
              <div className="text-xs text-[#4c3f34]/70 flex items-center gap-2"><Clock size={12} /> Step 5 · Time capsule</div>
              <button
                onClick={() => setStep("seal")}
                disabled={!deliverAt}
                data-testid="capsule-next-btn"
                className="text-sm px-5 py-2 rounded-full bg-[#1C1917] text-[#FDFBF7] disabled:opacity-40 hover:bg-[#3f342c] transition-colors"
              >
                Lock and continue
              </button>
            </div>

            <h2 className="font-serif-elegant text-4xl md:text-5xl text-[#1C1917]">When should this open?</h2>
            <p className="mt-3 text-[#4c3f34]">The letter will stay sealed until this exact moment. Not a second sooner.</p>

            <div className="mt-8 flex flex-wrap gap-3">
              {DATE_PRESETS.map((p) => {
                const target = new Date(Date.now() + p.days * 24 * 3600 * 1000).toISOString();
                const active = deliverAt === target;
                return (
                  <button
                    key={p.id}
                    data-testid={`capsule-preset-${p.id}`}
                    onClick={() => setDeliverAt(target)}
                    className={`px-5 py-2.5 rounded-full text-sm border transition-all hover:-translate-y-0.5 ${
                      active
                        ? "bg-[#991B1B] text-white border-transparent"
                        : "bg-[#FDFBF7] text-[#1C1917] border-[#1C1917]/15 hover:border-[#991B1B]/40"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-8">
              <label className="block text-xs uppercase tracking-widest text-[#4c3f34] mb-2">Or pick a custom date</label>
              <input
                data-testid="capsule-custom-date"
                type="date"
                min={new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10)}
                onChange={(e) => {
                  if (!e.target.value) return;
                  // set at 9am local
                  const d = new Date(e.target.value + "T09:00:00");
                  setDeliverAt(d.toISOString());
                }}
                className="bg-[#FDFBF7] border border-[#1C1917]/15 rounded-[4px] px-4 py-2.5 text-[#1C1917] outline-none focus:border-[#991B1B]"
              />
            </div>

            {deliverAt && (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="mt-10 rounded-[6px] bg-[#FDFBF7] border border-[#991B1B]/20 p-6 shadow-sm"
                data-testid="capsule-summary"
              >
                <div className="flex items-center gap-3 text-[#991B1B]">
                  <Lock size={16} />
                  <div className="uppercase tracking-widest text-xs">Sealed until</div>
                </div>
                <div className="mt-2 font-serif-elegant text-3xl md:text-4xl text-[#1C1917]">
                  {new Date(deliverAt).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </div>
                <p className="mt-2 text-sm text-[#4c3f34]">
                  You will not be able to open this letter before this date. Bookmark the link — a countdown will wait for you.
                </p>
              </motion.div>
            )}
          </motion.section>
        )}

        {step === "seal" && (
          <motion.section key="seal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-3xl mx-auto px-6 pb-24 text-center"
          >
            <h2 className="font-serif-elegant text-4xl md:text-5xl text-[#1C1917]">Ready to seal?</h2>
            <p className="mt-3 text-[#4c3f34]">Once sealed, the wax cools and the letter is on its way.</p>

            <div className="mt-14 flex justify-center">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.9 }}
              >
                <Envelope
                  sealed
                  recipient={recipientName}
                  sender={senderName}
                  accent={emo.accent}
                  stamp={stamp}
                  size="lg"
                  pulse
                />
              </motion.div>
            </div>

            <div className="mt-12 flex justify-center gap-4">
              <button onClick={goBack} className="px-6 py-3 rounded-full border border-[#1C1917]/20 text-[#1C1917] hover:bg-[#1C1917]/5" data-testid="seal-back-btn">
                Not yet
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="seal-send-btn"
                className="px-8 py-3 rounded-full bg-[#991B1B] text-white hover:bg-[#7F1D1D] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Heart size={16} fill="#fff" />}
                {submitting
                  ? "Opening secure checkout…"
                  : `Send with love · ₹${currentLetterPriceInr()}`}
              </button>
            </div>
            <p className="mt-4 text-xs text-[#4c3f34]/70">
              Secure payment by Razorpay · UPI, Cards, Wallets & Netbanking · INR
            </p>
          </motion.section>
        )}

        {step === "delivering" && (
          <DeliveringAnimation onComplete={() => setStep("share")} />
        )}

        {step === "share" && slug && (
          <ShareStep
            key="share"
            slug={slug}
            link={link}
            manageLink={manageLink}
            recipient={recipientName}
            isCapsule={isCapsule}
            deliverAt={deliverAt}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ----- Voice recorder ----- */
function VoiceRecorder({ voice, setVoice, uploadFn }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setUploading(true);
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        const res = await uploadFn(file);
        setUploading(false);
        if (res) setVoice({ ...res, previewUrl: URL.createObjectURL(blob) });
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone access is required.");
    }
  };

  const stop = () => {
    mediaRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  return (
    <div className="rounded-[4px] bg-[#FDFBF7] border border-[#1C1917]/10 p-6">
      <div className="flex items-center gap-3 text-[#1C1917]">
        <Mic size={18} /><span className="font-serif-elegant text-2xl">Your voice</span>
      </div>
      <p className="text-sm text-[#4c3f34] mt-2">A short recording plays inside the letter, when they are ready to listen.</p>

      <div className="mt-5">
        {!voice && !recording && (
          <button
            data-testid="mem-voice-record-btn"
            onClick={start}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#991B1B] text-white hover:bg-[#7F1D1D] transition-colors text-sm"
          >
            <Mic size={14} /> Record voice
          </button>
        )}
        {recording && (
          <div className="flex items-center gap-4">
            <span className="inline-flex w-3 h-3 rounded-full bg-[#991B1B] animate-pulse" />
            <span className="text-[#1C1917] tabular-nums">{formatTime(elapsed)}</span>
            <button
              data-testid="mem-voice-stop-btn"
              onClick={stop}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1C1917] text-[#FDFBF7] hover:bg-[#3f342c] text-sm"
            >
              <Square size={12} /> Stop
            </button>
          </div>
        )}
        {uploading && <div className="text-sm text-[#4c3f34] mt-2 flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Uploading</div>}
        {voice && (
          <div className="mt-3 flex items-center gap-3">
            <audio data-testid="mem-voice-audio" controls src={voice.previewUrl} className="max-w-full" />
            <button
              data-testid="mem-voice-delete-btn"
              onClick={() => setVoice(null)}
              className="p-2 rounded-full bg-[#1C1917]/5 hover:bg-[#1C1917]/10 text-[#1C1917]"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

/* ----- Share step ----- */
function ShareBtn({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="group w-full aspect-[3/2] rounded-[4px] bg-[#FDFBF7] border border-[#1C1917]/10 hover:border-[#991B1B]/40 hover:-translate-y-0.5 transition-all flex flex-col items-center justify-center gap-2 shadow-sm"
    >
      <span className="w-11 h-11 rounded-full flex items-center justify-center bg-[#991B1B]/10 text-[#991B1B]">
        <Icon size={18} />
      </span>
      <span className="text-sm text-[#1C1917]">{label}</span>
    </button>
  );
}

function ShareStep({ slug, link, manageLink, recipient, isCapsule, deliverAt }) {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const message = `${recipient || "You"}, I wrote something for you. Open it here: ${link}`;

  const track = (ch) => trackShare(slug, ch).catch(() => {});
  const openWA = () => { track("whatsapp"); window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank"); };
  const openEmail = () => { track("email"); window.location.href = `mailto:?subject=${encodeURIComponent("A letter for you")}&body=${encodeURIComponent(message)}`; };
  const openSMS = () => { track("sms"); window.location.href = `sms:?body=${encodeURIComponent(message)}`; };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
      track("copy_link");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };
  const showQr = () => { track("qr"); setShowQR(true); };

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=${encodeURIComponent(link)}`;

  return (
    <motion.section
      key="share"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="max-w-3xl mx-auto px-6 pb-24 text-center"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.7 }}
        className="mx-auto w-16 h-16 rounded-full bg-[#991B1B]/15 flex items-center justify-center mb-6"
      >
        <Heart className="text-[#991B1B]" fill="#991B1B" size={26} />
      </motion.div>
      <h2 className="font-serif-elegant text-4xl md:text-5xl text-[#1C1917]">
        {isCapsule ? "Your time capsule is sealed." : "Your letter is sealed."}
      </h2>
      <p className="mt-3 text-[#4c3f34]">
        {isCapsule
          ? `Bookmark this link. A countdown will wait for you until ${new Date(deliverAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.`
          : `Share this private link with ${recipient || "someone special"}. Only they can open it.`}
      </p>

      <div className="mt-8 mx-auto max-w-xl">
        <div className="rounded-full bg-[#FDFBF7] border border-[#1C1917]/15 px-5 py-3 flex items-center gap-3">
          <div className="truncate text-sm text-[#1C1917]" data-testid="share-link-text">{link}</div>
          <button data-testid="share-copy-btn" onClick={copy} className="px-4 py-1.5 text-xs rounded-full bg-[#1C1917] text-[#FDFBF7] hover:bg-[#3f342c] transition-colors">
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        {!isCapsule && manageLink && (
          <div className="mt-4 rounded-full bg-[#F5EEDD] border border-[#B8935A]/30 px-5 py-3 flex items-center gap-3">
            <div className="truncate text-xs text-[#4c3f34] uppercase tracking-widest shrink-0">Your private key</div>
            <div className="truncate text-sm text-[#1C1917]" data-testid="share-manage-link">{manageLink}</div>
            <button
              data-testid="share-manage-copy-btn"
              onClick={async () => { await navigator.clipboard.writeText(manageLink); toast.success("Reactions link copied"); }}
              className="px-4 py-1.5 text-xs rounded-full bg-[#B8935A] text-white hover:bg-[#8b6a3a]"
            >
              Copy
            </button>
          </div>
        )}
      </div>

      {!isCapsule && manageLink && (
        <p className="mt-3 text-xs text-[#4c3f34]/70 max-w-md mx-auto">
          Keep the second link private — visit it later to see how {recipient || "they"} reacted. Only you have it.
        </p>
      )}

      <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-3">
        <ShareBtn icon={MessageCircle} label="WhatsApp" onClick={openWA} testId="share-whatsapp" />
        <ShareBtn icon={Mail} label="Email" onClick={openEmail} testId="share-email" />
        <ShareBtn icon={MessageSquare} label="SMS" onClick={openSMS} testId="share-sms" />
        <ShareBtn icon={QrCode} label="QR Code" onClick={showQr} testId="share-qr" />
        <ShareBtn icon={Copy} label="Copy Link" onClick={copy} testId="share-copy-btn2" />
      </div>

      <div className="mt-10 flex items-center justify-center gap-4">
        <Link
          to={`/letter/${slug}`}
          data-testid="share-preview-btn"
          className="px-6 py-3 rounded-full border border-[#1C1917]/20 hover:bg-[#1C1917]/5 text-[#1C1917] text-sm"
        >
          Preview someone special's view
        </Link>
        <Link to="/compose" data-testid="share-another-btn" className="px-6 py-3 rounded-full bg-[#991B1B] text-white hover:bg-[#7F1D1D] text-sm">
          Write another
        </Link>
      </div>

      <AnimatePresence>
        {showQR && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#1C1917]/50 backdrop-blur-sm flex items-center justify-center z-50 p-6"
            onClick={() => setShowQR(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-[#FDFBF7] text-[#1C1917] rounded-[6px] p-6 max-w-sm w-full text-center relative"
              onClick={(e) => e.stopPropagation()}
              data-testid="share-qr-modal"
            >
              <button
                data-testid="share-qr-close"
                onClick={() => setShowQR(false)}
                className="absolute top-3 right-3 text-[#1C1917]/60 hover:text-[#1C1917]"
              >
                <X size={18} />
              </button>
              <div className="font-serif-elegant text-2xl">Scan to open</div>
              <img src={qrUrl} alt="QR code" className="mx-auto mt-4 rounded-[3px]" />
              <p className="text-xs text-[#4c3f34] mt-3">Someone special can scan this on their phone to open the letter.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}
